import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useCreateAgentEligibilityQuery$data,
	useCreateAgentEligibilityQuery$variables,
} from './__generated__/useCreateAgentEligibilityQuery.graphql.ts';
import { useCreateAgentBaseEligibility } from './useCreateAgentBaseEligibility.ts';

type UseCreateAgentEligibilityArgs = {
	source: string;
	skip: boolean;
};

export type UseCreateAgentEligibilityResult = {
	isEligible: boolean;
	isLoading: boolean;
};

export const useCreateAgentEligibility = ({
	source,
	skip,
}: UseCreateAgentEligibilityArgs): UseCreateAgentEligibilityResult => {
	const { cloudId } = useSessionData();
	const { hasBaseEligibility } = useCreateAgentBaseEligibility();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const shouldCheckAgentsPermissions = hasBaseEligibility && !skip;

	const { data, loading } = useQueryInterop<
		useCreateAgentEligibilityQuery$data,
		useCreateAgentEligibilityQuery$variables
	>(
		graphql`
			query useCreateAgentEligibilityQuery($cloudId: ID!) {
				atlassianStudio_userSiteContext(cloudId: $cloudId) {
					... on AtlassianStudioUserSiteContextOutput {
						userPermissions {
							isAbleToCreateAgents
						}
					}
				}
			}
		`,
		{
			variables: { cloudId: cloudId ?? '' },
			skip: !shouldCheckAgentsPermissions,
			// Avoids re-fetching and re-showing loading on later menu opens.
			fetchPolicy: 'cache-first',
			interopApolloAggClient: true,
			onCompleted: (resp) => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source,
						action: 'success',
						actionSubject: 'studioPermissionsQuery',
						actionSubjectId: 'confluenceCreateMenu',
						attributes: {
							isAbleToCreateAgents:
								resp?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents,
						},
					},
				}).fire();
			},
			onError: (error) => {
				if (
					!isErrorMarkedAsHandled(error) &&
					(isUnauthorizedError(error) || error?.message.includes('Failed to fetch'))
				) {
					markErrorAsHandled(error);
					return;
				}

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source,
						action: 'error',
						actionSubject: 'studioPermissionsQuery',
						actionSubjectId: 'confluenceCreateMenu',
						attributes: {
							errorMessage: error.message,
						},
					},
				}).fire();
			},
		},
	);

	const hasPermission =
		data?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents ?? false;

	return {
		isEligible: Boolean(shouldCheckAgentsPermissions && hasPermission),
		isLoading: shouldCheckAgentsPermissions && loading,
	};
};
